import { Client } from '@stomp/stompjs';
import { tokenPainel } from './painelDispositivo';

// Conexão STOMP sobre WebSocket nativo (endpoint SockJS do back-end também
// expõe o transporte websocket puro em /ws/websocket).
const brokerURL = () => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // Em desenvolvimento o CRA roda em :3000 e o back em :8080 (proxy do CRA
    // não cobre WebSocket de forma confiável — conecta direto).
    const host = process.env.NODE_ENV === 'development'
        ? `${window.location.hostname}:8080`
        : window.location.host;
    return `${proto}://${host}/ws/websocket`;
};

// O CONNECT do STOMP passou a exigir token (achado 9 da auditoria: assinar
// /topic/fila não pedia credencial e o payload leva nome e placa). No app web o
// token vem do cookie httpOnly, que o navegador anexa no handshake e o back lê
// ali — nada a fazer aqui. No quiosque da TV não há cookie, então vai o token de
// dispositivo no header do frame.
const cabecalhosConexao = () => {
    const token = tokenPainel();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Assina o tópico informado e chama onEvento a cada evento da fila.
// onStatus (opcional) recebe true/false quando a conexão sobe/cai.
// Retorna função de desconexão para uso em useEffect.
const conectarTopico = (topico, onEvento, onStatus) => {
    const client = new Client({
        brokerURL: brokerURL(),
        connectHeaders: cabecalhosConexao(),
        reconnectDelay: 3000,
        onConnect: () => {
            onStatus?.(true);
            client.subscribe(topico, (mensagem) => {
                try {
                    onEvento(JSON.parse(mensagem.body));
                } catch {
                    // mensagem inválida — ignora
                }
            });
        },
        onWebSocketClose: () => onStatus?.(false),
    });
    client.activate();
    return () => client.deactivate();
};

export const conectarFila = (onEvento, onStatus) => conectarTopico('/topic/fila', onEvento, onStatus);

export const conectarFilaCarga = (onEvento, onStatus) => conectarTopico('/topic/fila-carga', onEvento, onStatus);

export const conectarSupportLogs = (onEvento, onStatus) => conectarTopico('/topic/support/logs', onEvento, onStatus);

// Pesagem: o peso chega segundos depois da solicitação, quando o agente termina
// de ler a serial. É o mesmo motivo da fila não ter polling — a tela fica
// esperando um evento, não perguntando de novo.
export const conectarPesagem = (onEvento, onStatus) => conectarTopico('/topic/pesagem', onEvento, onStatus);

// Mapa do pátio (issue #720): aviso sem conteúdo de que uma integração do
// IntegraHub mudou — presença, descarga, estado ou só o pulso de vida.
export const conectarPresencaHub = (onEvento, onStatus) => conectarTopico('/topic/integrahub-presenca', onEvento, onStatus);

// Diagnóstico da serial no cadastro da balança: tópico por pedido, criado pela
// própria tela. Assina-se antes de pedir a leitura — tópico STOMP não guarda
// mensagem, então assinar depois perderia o resultado de uma escuta curta.
export const conectarDiagnosticoBalanca = (diagnosticoId, onEvento, onStatus) =>
    conectarTopico(`/topic/balancas/diagnostico/${diagnosticoId}`, onEvento, onStatus);

// MPG Assist: tópico por conversa, não por usuário — só o dono ou quem tem
// ASSIST_ATENDER pode assinar (AutorizadorTopicoAssist, no back). Assina-se
// depois de abrir/continuar a conversa, quando o id já existe.
export const conectarAssist = (conversaId, onEvento, onStatus) =>
    conectarTopico(`/topic/assist/${conversaId}`, onEvento, onStatus);

// Aviso do painel de atendimento: só "algo mudou", nunca conteúdo de
// conversa — quem escuta busca a lista de novo pela API. Fora do prefixo de
// conversa de propósito (fase 7, #434): autorizado por ASSIST_ATENDER, não
// por identidade.
export const conectarAssistPainel = (onEvento, onStatus) =>
    conectarTopico('/topic/assist-painel', onEvento, onStatus);

// Avisos de sugestão da fila (issue #568): tópico separado do da fila porque
// o painel da TV assina aquele e não deve receber isto. Só quem opera a fila
// consegue assinar (WebSocketAuthInterceptor).
export const conectarAvisosSugestao = (tipo, onEvento, onStatus) =>
    conectarTopico(tipo === 'CARGA' ? '/topic/fila-carga/avisos' : '/topic/fila/avisos', onEvento, onStatus);

// "O diagnóstico da área mudou" (issue #622), sem conteúdo: a tela da fila
// pergunta ao servidor se há pergunta de um toque para quem opera.
export const conectarDiagnosticoOperacao = (area, onEvento, onStatus) =>
    conectarTopico(area === 'CARGA' ? '/topic/operacao/carga' : '/topic/operacao/descarga', onEvento, onStatus);
